/**
 * AuthAdminCallback · v2.7.65+E69
 * ══════════════════════════════════════════════════════════════════════
 * Landing page after the Emergent-managed Google Auth redirect for the
 * ADMIN / STAFF portal. Parses `#session_id=...` from the URL fragment,
 * POSTs it to `/api/auth/google/admin-session`, and:
 *   • On success → stash JWT + admin user in localStorage (matching the
 *     AdminContext persist shape) and redirect to /admin.
 *   • On 2FA challenge → route to /admin/login/2fa with the pending token.
 *   • On 403 not_authorized_for_admin → clear message, bounce to
 *     /admin/login (this portal is LINK-ONLY — unknown Google emails
 *     are never auto-created).
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Shield } from "lucide-react";
import { broadcastLogin } from "@/lib/authSync";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function AuthAdminCallback() {
  const nav = useNavigate();
  const ranRef = useRef(false);
  const [twofa, setTwofa] = useState({ pendingToken: null, code: "", busy: false });

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const fragment = window.location.hash.startsWith("#") ? window.location.hash.slice(1) : window.location.hash;
    const params = new URLSearchParams(fragment);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      toast.error("Missing session id — please try Google sign-in again");
      nav("/admin/login", { replace: true });
      return;
    }
    api.post("/auth/google/admin-session", {}, {
      headers: { "X-Session-ID": sessionId },
    })
      .then((r) => {
        try {
          window.history.replaceState({}, document.title, "/admin");
        } catch (e) {
          console.warn("AuthAdminCallback history.replaceState blocked", e?.message);
        }
        const data = r.data;
        if (data?.requires_2fa) {
          setTwofa({ pendingToken: data.pending_token, code: "", busy: false });
          return;
        }
        if (!data?.access_token || !data?.user) {
          toast.error("Google sign-in failed — malformed response");
          nav("/admin/login", { replace: true });
          return;
        }
        try {
          localStorage.setItem("rtg_admin_token", data.access_token);
          localStorage.setItem("rtg_admin", JSON.stringify(data.user));
          broadcastLogin("admin", { token: data.access_token, user: data.user });
        } catch (_e) { /* private browsing */ }
        toast.success(`Welcome ${data.user?.name?.split(" ")[0] || "operator"}!`);
        window.location.assign("/admin");
      })
      .catch((e) => {
        const detail = e?.response?.data?.detail;
        if (e?.response?.status === 403 && typeof detail === "object" && detail?.code === "not_authorized_for_admin") {
          toast.error(detail.message || "This Google account is not authorized for staff access", { duration: 8000 });
        } else {
          const msg = typeof detail === "string" ? detail : (detail?.message || "Google sign-in failed");
          toast.error(msg);
        }
        nav("/admin/login", { replace: true });
      });
  }, [nav]);

  const submit2fa = async () => {
    if (!twofa.pendingToken || !twofa.code) return;
    setTwofa((t) => ({ ...t, busy: true }));
    try {
      const { data } = await api.post("/admin/login/2fa-verify", {
        pending_token: twofa.pendingToken,
        code: twofa.code.trim(),
      });
      if (!data?.access_token || !data?.user) {
        toast.error("2FA succeeded but response was malformed");
        nav("/admin/login", { replace: true });
        return;
      }
      try {
        localStorage.setItem("rtg_admin_token", data.access_token);
        localStorage.setItem("rtg_admin", JSON.stringify(data.user));
        broadcastLogin("admin", { token: data.access_token, user: data.user });
      } catch (_e) { /* noop */ }
      window.location.assign("/admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid 2FA code");
      setTwofa((t) => ({ ...t, code: "", busy: false }));
    }
  };

  if (twofa.pendingToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0c0d10] text-zinc-100 p-6" data-testid="auth-admin-callback-2fa">
        <div className="w-full max-w-sm space-y-4 text-center">
          <Shield className="h-10 w-10 mx-auto text-amber-400"/>
          <div>
            <div className="font-bold text-zinc-100 text-lg">Two-factor authentication</div>
            <div className="text-xs text-zinc-500 mt-1">Enter the 6-digit code from your authenticator app, or a one-time backup code.</div>
          </div>
          <Input
            autoFocus
            data-testid="google-admin-2fa-code"
            value={twofa.code}
            onChange={(e) => setTwofa((t) => ({ ...t, code: e.target.value }))}
            onKeyDown={(e) => e.key === "Enter" && submit2fa()}
            placeholder="123456 or backup code"
            className="bg-zinc-950 border-zinc-700 h-12 text-center text-lg tracking-[0.4em]"
          />
          <Button
            data-testid="google-admin-2fa-submit"
            onClick={submit2fa}
            disabled={twofa.busy || !twofa.code.trim()}
            className="w-full h-12 bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold"
          >
            {twofa.busy ? <Loader2 className="h-4 w-4 animate-spin"/> : "VERIFY & CONTINUE"}
          </Button>
          <button
            type="button"
            onClick={() => nav("/admin/login", { replace: true })}
            className="text-[10px] uppercase tracking-wider text-zinc-500 hover:text-zinc-300"
            data-testid="google-admin-2fa-cancel"
          >
            ← back to admin login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0c0d10] text-zinc-100" data-testid="auth-admin-callback">
      <div className="text-center">
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-amber-400"/>
        <div className="mt-3 text-sm text-zinc-400">Completing staff sign-in…</div>
      </div>
    </div>
  );
}
